import { useMemo } from "react";
import useSWR from 'swr';
import { DEFAULT_AXIOS_CONFIG, fetcher } from './../index';
import {
	BackendCampaign,
	CampaignArticles,
	ICampaign,
	MultilingualFile
} from './../../interfaces/marketing/campaigns';
import { LATEST_VERSION, transformCampaign } from './index';
import axios from 'axios';

const getMultilingualFile = (file: any): MultilingualFile => {
	if (file.multilingual) {
		return {
			multilingual: true,
			nl: file.nl
		};
	}

	return {
		multilingual: false,
		nl: file.nl,
		fr: file.fr
	};
};

const getArticlesData = (c: ICampaign): CampaignArticles => ({
	badge: c.hasBadge && c.articles?.badge ? {
		text: c.articles?.badge?.text,
		color: c.articles?.badge?.color,
		image: c.badgeHasImage ? getMultilingualFile(c.articles?.badge?.image) : undefined
	} : undefined,
	dmz: c.articles?.dmz,
	ids: c.articles?.ids,
	start: c.articlesHasDeviatingDates ? c.articles?.start : undefined,
	end: c.articlesHasDeviatingDates ? c.articles?.end : undefined
});

const getVersion1 = (campaign: ICampaign): any => {
	return {
		id: campaign.id,
		badge: {
			nl: campaign.articles?.badge?.text?.nl,
			fr: campaign.articles?.badge?.text?.fr,
			color: {
				text: campaign.articles?.badge?.color?.text,
				bg: campaign.articles?.badge?.color?.background,
			}
		},
		filter: {
			nl: campaign.articles?.filter?.text?.nl,
			fr: campaign.articles?.filter?.text?.fr
		},
		articles: campaign.articles?.ids,
		start: campaign.start,
		end: campaign.end,
		active: campaign.active,
		weight: 0,
		version: LATEST_VERSION
	};
};

const getSaveableCampaign = (c: ICampaign): BackendCampaign => ({
	id: c.id,
	active: c.active ? 1 : 0,
	data: {
		...getVersion1(c),
		version2: {
			id: c.id,
			name: c.name,
			start: c.start,
			end: c.end,
			version: LATEST_VERSION,
			active: c.active,
			articles: getArticlesData(c),
			inThePicture: true
		}
	}
});

export const saveCampaign = async (c: ICampaign) => {
	try {
		const campaign = getSaveableCampaign(c);
		const result = await axios.post(`/sales/campaigns/${c.id}`, campaign, DEFAULT_AXIOS_CONFIG);
		return result?.data;
	} catch (error) {
		return error;
	}
};

export const deleteCampaign = async (id: string) => {
	try {
		const result = await axios.post(`/sales/campaigns/${id}/delete`, null, DEFAULT_AXIOS_CONFIG);
		return result?.data;
	} catch (error) {
		return error;
	}
}

const DEFAULT_NEW_CAMPAIGN = {
	id: 'new',
	version: LATEST_VERSION,
	name: {
		nl: '',
		fr: ''
	},
	start: new Date(),
	end: new Date(),
	weight: 0,
	active: false,
	articles: {
		badge: {
			color: {
				text: '#ffffff',
				background: '#675583'
			},
			text: {
				fr: 'À l\'honneur',
				nl: 'In de kijker'
			}
		}
	},
	documents: undefined,
	banners: undefined,
	hasArticles: false,
	hasBadge: true,
	badgeHasImage: false,
	badgeImageIsMultilingual: false,
	hasFilter: false,
	articlesHasDeviatingDates: false,
	hasDocuments: false,
	hasBanners: false
};

export default function useCampaign(id: string | null): { campaign?: ICampaign, error: any, mutate: any } {
	const { data, error, mutate } = useSWR(id && `/sales/campaigns/${id}`, fetcher);

	const campaign = useMemo(() => data ? transformCampaign(data) : undefined, [data]);

	const emptyCampaign = useMemo(() => DEFAULT_NEW_CAMPAIGN, []);

	return {
		campaign: id ? campaign : emptyCampaign,
		error,
		mutate
	};
}
